import type { ContactSessionStatus } from "@wags/profile-contracts/contact-session.contract.ts";
import {
    type ContactVerificationStatus,
    contactSessionFailureMessage,
} from "./contact-session-client";
import { KITS_RECEIVED_SESSION_STATUSES } from "./kits-received-statuses";

export type ContactLayout = "sms" | "inline" | "qr";

export type ContactMode = "loading" | ContactLayout;

export type ContactSheetStage = "compose" | "returning" | "handedOff" | "sent";

export type CopyState = "idle" | "copied" | "error";

export function contactLayout(
    canUseSmsHandoff: boolean,
    handoffActive: boolean,
): ContactLayout {
    if (!canUseSmsHandoff) return "qr";
    return handoffActive ? "sms" : "inline";
}

export function contactSheetStage({
    messagesTapped,
    verificationStatus,
    sessionStatus,
}: {
    messagesTapped: boolean;
    verificationStatus: ContactVerificationStatus | undefined;
    sessionStatus?: ContactSessionStatus;
}): ContactSheetStage {
    if (messagesTapped) {
        return verificationStatus === "verified" ? "sent" : "handedOff";
    }
    return kitsReceivedText(verificationStatus, sessionStatus)
        ? "returning"
        : "compose";
}

export function kitsReceivedText(
    verificationStatus: ContactVerificationStatus | undefined,
    sessionStatus: ContactSessionStatus | undefined,
) {
    return (
        verificationStatus === "verified" ||
        (sessionStatus !== undefined &&
            KITS_RECEIVED_SESSION_STATUSES.some(
                (status) => status === sessionStatus,
            ))
    );
}

export function contactStage(
    mode: ContactMode,
    progress: Parameters<typeof contactSheetStage>[0],
): ContactSheetStage {
    return mode === "sms" ? contactSheetStage(progress) : "compose";
}

export function stagePaneKey(stage: ContactSheetStage) {
    return stage === "returning" ? "compose" : stage;
}

export function showContactFooter(
    mode: ContactMode,
    stage: ContactSheetStage,
    canFinish: boolean,
) {
    if (mode === "loading") return true;
    if (mode === "qr") return false;
    return stage !== "sent" || canFinish;
}

export function contactFooterLabel(
    stage: ContactSheetStage,
    preparing: boolean,
) {
    if (stage === "sent") return "Done";
    if (preparing) return "Preparing Messages…";
    return stage === "handedOff" ? "Open Messages" : "Send in Messages";
}

export function contactCopyState({
    copyFailed,
    copied,
}: {
    copyFailed: boolean;
    copied: boolean;
}): CopyState {
    if (copyFailed) return "error";
    return copied ? "copied" : "idle";
}

export function contactHandoffNotices({
    mode,
    sessionLoading,
    hasSession,
    actionError,
    sessionError,
}: {
    mode: ContactMode;
    sessionLoading: boolean;
    hasSession: boolean;
    actionError: string | null;
    sessionError: Error | null;
}) {
    return {
        pendingMessage:
            mode === "qr" && sessionLoading && !hasSession
                ? "Setting up your message…"
                : null,
        errorMessage:
            actionError ??
            (sessionError == null
                ? null
                : contactSessionFailureMessage(sessionError)),
    };
}
